import mustache from "mustache";
import { BaseLoginResponse } from "../../../base/response/BaseLoginResponse";
import { IConnectRender } from "../../definitions/IConnectRender";

import template from "./template.html";
import { AppConfig } from "../../definitions/app/AppConfig";
import { secureStorage } from "../../../modulos/seguridad/login/services/SecureStorageService";
import { ILayoutService } from "../../../abstractions/services/ILayoutService";
import { IPage } from "../../definitions/page/IPage";
import { serviceProvider } from "../../../comun/provider/ServiceProvider";
import { ConfirmModalRender } from "../modal/ConfirmModalRender";

mustache.parse(template);

export class AppRender<TLoginResponse extends BaseLoginResponse>
  implements IConnectRender, ILayoutService
{
  private readonly _config: AppConfig<TLoginResponse>;

  constructor(config: AppConfig<TLoginResponse>) {
    this._config = config;
    serviceProvider.setLayoutService(this);
  }

  private _currentPage: IPage | null = null;

  navigate(route: string): void {
    this._sidebarWrapper?.classList.remove("open");
    //
    this._secondSidebarWrapper!.innerHTML = "";
    this._pageContent!.innerHTML = "";
    //
    this._currentPage?.dispose();

    // Sincroniza el resaltado del menú con la ruta (navegación programática
    // incluida: botones "volver", código, etc.).
    this._config.asideMenuRender.onNavigate?.(route);

    this._currentPage = this._config.asideMenuRender.getPage(route);
    if (this._currentPage) this._mountPage(this._currentPage);
  }

  /** Re-renderiza la página actual (misma instancia): la dispone primero para
   *  liberar su estado interno (mapa, sub-renders, listeners) antes de que
   *  `render()` lo reconstruya. */
  reload(): void {
    if (!this._currentPage) return;
    this._currentPage.dispose();
    this._mountPage(this._currentPage);
  }

  setTitle(title: string): void {
    if (this._topbarTitle) {
      this._topbarTitle.innerHTML = title;
    }
  }

  /** HTML del layout: sidebar con el menú, topbar, aviso opcional (`AppConfig.aviso`) y contenedores de página. */
  render(): string {
    return mustache.render(template, {
      aviso: this._config.aviso ?? "",
      brand: this._config.brand,
      usuario: this._config.loginResponse.nombre,
      navContent: this._config.asideMenuRender.render(
        this._config.loginResponse,
      ),
      title: this._config.defaultTitle,
    });
  }

  connect(): void {
    document.querySelectorAll<HTMLButtonElement>(".sign-out").forEach((btn) => {
      btn.addEventListener("click", () => {
        new ConfirmModalRender({
          title: "Cerrar sesión",
          message: "¿Está seguro que desea cerrar la sesión?",
          confirmLabel: "Cerrar sesión",
          onConfirm: () =>
            secureStorage.remove().finally(() => window.location.reload()),
        }).init();
      });
    });

    this._sidebarWrapper = document.getElementById(
      "sidebarWrapper",
    ) as HTMLDivElement;
    document.getElementById("open-menu")?.addEventListener("click", () => {
      this._sidebarWrapper?.classList.add("open");
    });
    document.getElementById("close-menu")?.addEventListener("click", () => {
      this._sidebarWrapper?.classList.remove("open");
    });

    this._topbarTitle = document.getElementById("topbarTitle");
    this._secondSidebarWrapper = document.getElementById(
      "secondSidebarWrapper",
    ) as HTMLDivElement;
    this._pageContent = document.getElementById(
      "pageContent",
    ) as HTMLDivElement;

    this._config.asideMenuRender.connect(this._config.loginResponse);
  }

  dispose(): void {}

  private _sidebarWrapper: HTMLDivElement | null = null;
  private _topbarTitle: HTMLSpanElement | null = null;
  private _secondSidebarWrapper: HTMLDivElement | null = null;
  private _pageContent: HTMLDivElement | null = null;

  /** Monta (`render`) la página dada; no la dispone antes (eso es
   *  responsabilidad de quien la llama: `navigate()` ya dispuso la anterior,
   *  `reload()` dispone la misma instancia antes de llamar acá). */
  private _mountPage = (page: IPage) => {
    if (!this._pageContent) return;
    if (!this._secondSidebarWrapper) return;

    page.render(this._pageContent, this._secondSidebarWrapper);
  };
}
